import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { Stack } from "@seneca/senekit";

import { pushProviderToGlobalDataLayer } from "services/firebase/auth/analytics/events";
import { InvalidSignInMethod } from "services/firebase/auth/errors";
import firebaseErrorTranslator, {
  DEFAULT_MESSAGE,
  ErrorMessage,
  shouldLogError
} from "services/firebase/auth/errorTranslator";
import { ProviderSSO } from "services/firebase/auth/types";

import logError from "seneca-common/utils/sentry/logError";

import { makeInternalRedirectState } from "features/common/routing/InternalRedirectOnlyRoute";
import useIsGoStudentLearningApp from "features/goStudentLearning/hooks/useIsGoStudentLearningApp";
import { useIsPlurallApp } from "features/plurall";
import { getHelpLinkRegionCode } from "features/regions/selectors";
import useSignUpJourneyStateOnMount from "features/sign-up/global-sign-up-modal/hooks/useSignUpJourneyStateOnMount";
import {
  makeInternalLink,
  pathEndings
} from "features/sign-up/global-sign-up-modal/utils";
import signInWithProvider from "features/user/actions/auth/sso/signInWithProvider";
import { isLoadedFromTeams } from "features/user/actions/auth/sso/utils";

import useOnSignUpSuccess from "../../hooks/useOnSignUpSuccess";
import useRedirectOnSignInCallback from "../../hooks/useRedirectOnSignInCallback";
import {
  IconGoogle,
  IconGoStudent,
  IconMicrosoft,
  IconPlurall
} from "../IconsSSO";
import SignUpFormButton from "./SignUpFormButton";
import StudienkreisSignUpFormButton from "./StudienkreisSignUpFormButton";

type Props = {
  onSignInSuccess?: () => void;
  setErrorMessage: (errorMessage?: ErrorMessage) => void;
  accountType?: string;
  linkToNextPage?: string;
  redirectAuthenticatedUserTo?: string;
  search?: string;
};

export default function SignUpFormButtons({
  onSignInSuccess,
  setErrorMessage,
  accountType,
  linkToNextPage,
  redirectAuthenticatedUserTo,
  search
}: Props) {
  const [signInWithRedirect, setSignInWithRedirect] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<
    ProviderSSO | undefined
  >(undefined);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const isPlurallApp = useIsPlurallApp();
  const isGoStudentApp = useIsGoStudentLearningApp();
  const locale = useSelector(getHelpLinkRegionCode);

  const {
    redirectAuthenticatedUserTo: redirectAuthenticatedUserToFromState,
    search: searchFromState
  } = useSignUpJourneyStateOnMount();

  const onSignInSuccessRedirect = useRedirectOnSignInCallback(
    redirectAuthenticatedUserTo || redirectAuthenticatedUserToFromState
  );

  const onSignUpSuccess = useOnSignUpSuccess({
    accountType,
    linkToNextPage: linkToNextPage || makeInternalLink(pathEndings.userDetails),
    isSSO: true,
    search: search || searchFromState
  });

  const onPasswordConfirmation = ({ credential, email }: any) => {
    history.push(
      makeInternalRedirectState({
        pathname: makeInternalLink(pathEndings.confirmPassword),
        state: {
          ...location.state,
          auth: {
            credential,
            email
          }
        }
      } as any)
    );
  };

  const signInWith = async (
    provider: ProviderSSO,
    signInWithRedirect?: boolean
  ): Promise<void> => {
    try {
      setErrorMessage(undefined);
      setSelectedProvider(provider);
      pushProviderToGlobalDataLayer(provider);

      await dispatch(
        signInWithProvider({
          providerName: provider,
          signInWithRedirect,
          onPasswordConfirmation,
          onSignUpSuccess,
          onSignInSuccess: onSignInSuccess ?? onSignInSuccessRedirect
        })
      );
    } catch (error: any) {
      if (shouldLogError(error)) {
        logError(error, { message: "Unexpected error signing in via SSO" });
      }

      let errorMessage = firebaseErrorTranslator(error, locale);

      if (error instanceof InvalidSignInMethod && isPlurallApp) {
        delete errorMessage.link;
        delete errorMessage.linkText;
      }

      if (isLoadedFromTeams()) {
        return signInWith(provider, true);
      }

      if (errorMessage.message === DEFAULT_MESSAGE) {
        setSignInWithRedirect(true);
        errorMessage.message =
          "There seems to be an issue with displaying authentication pop-up in your browser. Please try again.";
      }

      setSelectedProvider(undefined);
      setErrorMessage(errorMessage);
    }
  };

  if (isGoStudentApp) {
    return (
      <Stack space="large">
        <SignUpFormButton
          icon={IconGoStudent}
          provider="gostudent"
          providerDisplayName="GoStudent Learning"
          signInWith={signInWith}
          selectedProvider={selectedProvider}
        />
        <StudienkreisSignUpFormButton
          signInWith={signInWith}
          selectedProvider={selectedProvider}
        />
      </Stack>
    );
  }

  if (isPlurallApp) {
    return (
      <SignUpFormButton
        icon={IconPlurall}
        provider="plurall"
        providerDisplayName="Plurall"
        signInWith={signInWith}
        selectedProvider={selectedProvider}
      />
    );
  }

  return (
    <Stack space="large">
      <SignUpFormButton
        icon={IconGoogle}
        provider="google"
        providerDisplayName="Google"
        signInWith={signInWith}
        signInWithRedirect={signInWithRedirect}
        selectedProvider={selectedProvider}
      />
      <SignUpFormButton
        icon={IconMicrosoft}
        provider="microsoft"
        providerDisplayName="Microsoft"
        signInWith={signInWith}
        signInWithRedirect={signInWithRedirect}
        selectedProvider={selectedProvider}
      />
    </Stack>
  );
}
